import type { ReactElement } from 'react';
import React, { useEffect } from 'react';
import ReactGA from 'react-ga';
import { Route, Routes, useLocation, useNavigationType } from 'react-router-dom';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';
import AuthenticatedRoutes from 'feature-components/routes/authenticated-routes';
import AuthorisedRoutes from 'feature-components/routes/authorised-routes';
import AuthenticatedLayout from 'layouts/authenticated';
import LoginGateway from 'pages/login';
import NotificationsPage from 'pages/notifications';
import OnBoardGateway from 'pages/on-board';
import OrganisationsGateway from 'pages/organisations';
import PasswordGateway from 'pages/password';
import ProfileGateway from 'pages/profile';
import RegistrationGateway from 'pages/register';

function GoogleAnalytics(props: React.PropsWithChildren<{ trackingId: string }>): ReactElement {
  const { trackingId, children } = props;
  const location = useLocation();

  ReactGA.initialize(trackingId, { debug: false });
  React.useEffect(() => {
    if (!trackingId) {
      return;
    }
    ReactGA.pageview(window.location.pathname + window.location.search);
  }, [trackingId, location]);

  return <>{children}</>;
}

export default function Root(): ReactElement {
  const type = useNavigationType();
  const location = useLocation();

  const getActionChange = (action: string): number => {
    if (action === 'PUSH') return 1;
    if (action === 'POP') return -1;
    return 0;
  };

  useEffect(() => {
    const backLength = +(sessionStorage.getItem('backHistory.length') || 0);
    sessionStorage.setItem(
      'backHistory.length',
      String(Math.max(backLength + getActionChange(type), 0))
    );
  }, [type, location]);

  return (
    <QueryParamProvider
      adapter={ReactRouter6Adapter}
      options={{ removeDefaultsFromUrl: true, enableBatching: true }}
    >
      <GoogleAnalytics trackingId={GOOGLE_ANALYTICS_TRACKING_ID}>
        <Routes>
          <Route element={<AuthenticatedRoutes />}>
            <Route path="/*" element={<AuthenticatedLayout />}>
              {/* Authenticated user */}
              <Route path="profile/*" element={<ProfileGateway />} />
              <Route path="notifications/*" element={<NotificationsPage />} />
              <Route path="organisations/*" element={<OrganisationsGateway />} />
              <Route path="on-board/*" element={<OnBoardGateway />} />

              <Route path="*" element={<AuthorisedRoutes />} />
            </Route>
          </Route>

          {/* login related routes can always be accessed */}
          <Route path="login/*" element={<LoginGateway />} />
          <Route path="register/*" element={<RegistrationGateway />} />
          <Route path="password/*" element={<PasswordGateway />} />
        </Routes>
      </GoogleAnalytics>
    </QueryParamProvider>
  );
}
